import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import moment, { type Moment } from "moment-timezone";
import { History as HistoryIcon } from "lucide-react";
import Dialog from "components/shared/Dialog";
import Tooltip from "components/shared/Tooltip";
import { useCompanyDateFormat, useCompanyTimeFormat } from "utils/hooks/company";
import { renderUserName } from "utils/helpers/user";
import { cn } from "lib/utils";

export interface Interval {
    from: string | Moment | Date | undefined;
    to: string | Moment | Date | undefined;
    available?: boolean;
}

export type HistoryAction = "requested" | "approved" | "denied" | "request-update" | "overwritten" | "request-deleted" | "superseded";

export interface HistoryEntry {
    action: HistoryAction;
    intervals?: Interval[];
    allDay: boolean;
    isAvailable?: boolean;
    type?: string | null;
    reason?: string;
    status?: string;
    user?: any;
    createdAt?: string;
    startDate?: string;
    endDate?: string;
    requestId?: string;
    note?: string | null;
}

interface HistoryProps {
    title: string;
    history: HistoryEntry[];
}

const ACTION_CLASSES: Record<HistoryAction, string> = {
    requested: "tw-bg-blue-100 tw-text-blue-700",
    approved: "tw-bg-green-100 tw-text-green-700",
    denied: "tw-bg-red-100 tw-text-red-700",
    "request-update": "tw-bg-amber-100 tw-text-amber-700",
    overwritten: "tw-bg-orange-100 tw-text-orange-700",
    "request-deleted": "tw-bg-gray-200 tw-text-gray-700",
    superseded: "tw-bg-purple-100 tw-text-purple-700",
};

function isCurrentlyActive(entry: HistoryEntry) {
    if ((entry.action !== "approved" && entry.action !== "superseded") || !entry.startDate) return false;
    const today = moment().startOf("day");
    const start = moment(entry.startDate).startOf("day");
    if (today.isBefore(start)) return false;
    if (!entry.endDate) return true;
    return today.isSameOrBefore(moment(entry.endDate).startOf("day"));
}

function availabilityText(entry: HistoryEntry, t: (key: string) => string) {
    const available = entry.isAvailable !== false;
    if (entry.allDay && available) return t("all-day-available");
    if (!entry.allDay && available) return t("not-all-day-available");
    if (!entry.allDay && !available) return t("not-all-day-unavailable");
    return t("all-day-unavailable");
}

function History({ title, history }: HistoryProps) {
    const { t } = useTranslation();
    const timeFormat = useCompanyTimeFormat();
    const dateFormat = useCompanyDateFormat();
    const [open, setOpen] = useState(false);

    const entries = useMemo(
        () => [...(history || [])].sort((a, b) => moment(b.createdAt).diff(moment(a.createdAt))),
        [history],
    );

    if (!entries.length) return null;

    return (
        <>
            <Tooltip
                trigger={
                    <button
                        type="button"
                        onClick={() => setOpen(true)}
                        className="tw-flex tw-items-center tw-text-primary hover:tw-opacity-80"
                    >
                        <HistoryIcon className="tw-size-4" />
                    </button>
                }
            >
                {t("history")}
            </Tooltip>

            <Dialog
                title={`${title} — ${t("history")}`}
                open={open}
                close={() => setOpen(false)}
                size="2xl"
                contentClassName="tw-overflow-y-auto tw-max-h-[90vh]"
                bodyClassName="tw-p-3 tw-min-h-0"
            >
                <div className="tw-flex tw-flex-col tw-gap-3 tw-max-h-[30vh] tw-overflow-y-auto tw-pr-1">
                    {entries.map((entry, i) => {
                        const available = entry.isAvailable !== false;
                        const validIntervals = (entry.intervals || []).filter((interval: Interval) =>
                            interval?.from && interval?.to && (typeof interval.available !== "boolean" || interval.available === available),
                        );
                        const active = isCurrentlyActive(entry);

                        return (
                            <div
                                key={entry.requestId ? `${entry.requestId}-${i}` : i}
                                className={cn(
                                    "tw-rounded-lg tw-border tw-p-3 tw-flex tw-flex-col tw-gap-2",
                                    active && "tw-border-green-500",
                                )}
                            >
                                <div className="tw-flex tw-items-center tw-justify-between tw-gap-2">
                                    <div className="tw-flex tw-items-center tw-gap-1.5">
                                        <span className={cn(
                                            "tw-rounded-full tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium",
                                            ACTION_CLASSES[entry.action] || "tw-bg-muted tw-text-muted-foreground",
                                        )}>
                                            {t(`${entry.action}`)}
                                        </span>
                                        {active && (
                                            <span className="tw-flex tw-items-center tw-gap-1 tw-rounded-full tw-bg-green-100 tw-px-2 tw-py-0.5 tw-text-xs tw-font-medium tw-text-green-700">
                                                <span className="tw-size-1.5 tw-rounded-full tw-bg-green-600" />
                                                {t("active")}
                                            </span>
                                        )}
                                    </div>
                                    {entry?.createdAt && (
                                        <span className="tw-text-[11px] tw-text-muted-foreground">
                                            {moment(entry.createdAt).format("YYYY-MM-DD HH:mm")}
                                        </span>
                                    )}
                                </div>

                                <span className="tw-text-sm tw-font-medium tw-text-foreground">
                                    {availabilityText(entry, t)}
                                </span>

                                {entry.startDate && (
                                    <p className="tw-text-xs tw-text-muted-foreground tw-m-0">
                                        {t("period")}:{" "}
                                        <span className="tw-text-foreground">
                                            {entry.endDate === entry.startDate
                                                ? moment(entry.startDate).format(dateFormat)
                                                : `${moment(entry.startDate).format(dateFormat)} – ${entry.endDate ? moment(entry.endDate).format(dateFormat) : t("ongoing")
                                                }`}
                                        </span>
                                    </p>
                                )}

                                {entry?.user && (
                                    <p className="tw-text-xs tw-text-muted-foreground tw-m-0">
                                        {t("by")}: <span className="tw-text-foreground">{renderUserName(entry.user)}</span>
                                    </p>
                                )}

                                {validIntervals.length > 0 && (
                                    <div className="tw-flex tw-flex-wrap tw-items-center tw-gap-x-4 tw-gap-y-1 tw-text-xs tw-text-muted-foreground">
                                        {validIntervals.map((s: Interval, si: number) => (
                                            <span key={si} className="tw-font-mono">
                                                {moment.parseZone(s.from as string).format(timeFormat)}
                                                {" – "}
                                                {moment.parseZone(s.to as string).format(timeFormat)}
                                            </span>
                                        ))}
                                    </div>
                                )}

                                {entry.reason && (
                                    <p className="tw-text-xs tw-text-muted-foreground tw-m-0">
                                        {t("reason")}: <span className="tw-text-foreground">{entry.reason}</span>
                                    </p>
                                )}

                                {entry.note && (
                                    <p className="tw-text-xs tw-text-muted-foreground tw-m-0">
                                        {t("note")}: <span className="tw-text-foreground">{entry.note}</span>
                                    </p>
                                )}
                            </div>
                        );
                    })}
                </div>
            </Dialog>
        </>
    );
}

export default History;
